import { useState } from 'react';
import '../../style.scss';
import { IPrivateIndividual } from '../../../../../models/partnerportalen/private-individual.model';
import ListItemDropDownMenu from '../../../../PaEgnaBen/Shared/ListItemDropDownMenu';
import { MenuItemType } from '../../../../PaEgnaBen/Shared/ListItemDropDownMenu/menu-item-type.enum';
import FocusTrap from '../../../../Shared/FocusTrap';
import { TranslationService } from '../../../../../services/translation.service';
import { AnalyticsService } from '../../../../../services/analytics-service';
import { Status } from '../../../../../hooks/fetch.hook';
import Alert from '../../../../Shared/Alert';
import { Icon } from '../../../../Shared/Icon';
import { IconMotif } from '../../../../Shared/Icon/icon-motif.enum';
import { IconColor } from '../../../../Shared/Icon/icon-color.enum';
import { IconVariant } from '../../../../Shared/Icon/icon-variant.enum';
import AntiforgeryService from '../../../../../services/anti-forgery.service';
import StralkastarenAdministratorHelper from '../../helpers/stralkastaren-administrator-helper';
import { Navigate } from 'react-router-dom';

const PERSONAL_IDENTITY_NUMBER_REGEX = /^\d{8}-?\d{4}$/;

function PrivateParticipations({ privateIndividuals, onDeleted }: { privateIndividuals: IPrivateIndividual[]; onDeleted: () => void }) {
  // === STATE ===
  const [deletePrivateIndividualStatus, setDeletePrivateIndividualStatus] = useState<{ status: Status; statusCode: number }>({ status: Status.notperformed, statusCode: 0 });
  const [selectedPrivateIndividual, setSelectedPrivateIndividual] = useState<IPrivateIndividual | undefined>(undefined);
  const [modalTriggerElement, setModalTriggerElement] = useState<string>('');
  const [privateIndividualFilter, setPrivateIndividualFilter] = useState('');
  const [exportStatus, setExportStatus] = useState<{ status: Status; statusCode: number }>({ status: Status.notperformed, statusCode: 0 });
  const [privateIndividualByPersonalIdentityNumber, setPrivateIndividualByPersonalIdentityNumber] = useState<IPrivateIndividual | undefined>(undefined);

  // === VARIABLES ===
  const filteredPrivateIndividuals =
    privateIndividualByPersonalIdentityNumber !== undefined
      ? [privateIndividualByPersonalIdentityNumber]
      : privateIndividuals.filter((individual) => {
          const searchTerm = privateIndividualFilter.trim().toLowerCase();
          return individual.email.toLowerCase().includes(searchTerm);
        });

  // === EVENT HANDLERS ===
  async function handleFilterChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setPrivateIndividualFilter(value);

    // If the entered search text is a valid personal identity number, look it up
    if (PERSONAL_IDENTITY_NUMBER_REGEX.test(value)) {
      try {
        const response = await fetch('/api/stralkastaren/administrator/private-individuals/lookup-by-personal-identity-number', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
          body: JSON.stringify({ personalIdentityNumber: value.replace('-', '') }),
        });
        if (response.ok) {
          const data = await response.json();
          setPrivateIndividualByPersonalIdentityNumber(data);
        } else {
          setPrivateIndividualByPersonalIdentityNumber(undefined);
        }
      } catch {
        setPrivateIndividualByPersonalIdentityNumber(undefined);
      }
    } else {
      setPrivateIndividualByPersonalIdentityNumber(undefined);
    }
  }

  async function handleConfirmDeleteClick() {
    if (!selectedPrivateIndividual) return;

    try {
      setDeletePrivateIndividualStatus({ status: Status.notperformed, statusCode: 0 });
      const response = await fetch(`/api/stralkastaren/administrator/private-individuals/${selectedPrivateIndividual.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
      });

      setDeletePrivateIndividualStatus({ status: response.ok ? Status.success : Status.failed, statusCode: response.status });
      if (response.ok) {
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Privatperson borttagen', '');
        onDeleted();
      } else {
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Privatperson borttagen - misslyckat', '');
      }
      setSelectedPrivateIndividual(undefined);
    } catch (error: unknown) {
      setDeletePrivateIndividualStatus({ status: Status.failed, statusCode: (error as { status?: number }).status || 0 });
    }
    setModalTriggerElement('');
  }

  function handleOnClose() {
    setSelectedPrivateIndividual(undefined);
    if (modalTriggerElement) {
      document.getElementById(modalTriggerElement)?.focus();
    }
  }

  async function handleOnExportClick() {
    try {
      setExportStatus({ status: Status.loading, statusCode: 0 });

      const response = await fetch(`/api/stralkastaren/administrator/private-individuals/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
      });

      if (response.ok) {
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = StralkastarenAdministratorHelper.getFileNameFromContentDispositionHeader(response.headers, 'stralkastaren-privatpersoner.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setExportStatus({ status: Status.success, statusCode: response.status });
        onDeleted();
      } else {
        setExportStatus({ status: Status.failed, statusCode: response.status });
      }
    } catch (error: unknown) {
      setExportStatus({ status: Status.failed, statusCode: (error as { status?: number }).status || 0 });
    }
  }

  // === RENDERING ===
  function renderDeleteModalContent() {
    return (
      <div className="modal-content">
        <div className="modal-header">
          <h2 id="sign-modal-title" className="modal-title fs-3">
            Ta bort privatperson
          </h2>
          <button type="button" className="btn-close" aria-label={TranslationService.get('common.close')} onClick={handleOnClose}></button>
        </div>
        <div className="modal-body pt-0">
          <div className="mb-3">
            Vill du ta bort <strong>{selectedPrivateIndividual?.email}</strong>?
          </div>
          <div className="d-grid d-md-flex gap-3">
            <button type="button" className="btn btn-secondary flex-grow-1 flex-basis-0 order-2 order-md-1" onClick={handleOnClose}>
              Avbryt
            </button>
            <button type="button" className="btn btn-primary flex-grow-1 flex-basis-0 order-1 order-md-2" onClick={handleConfirmDeleteClick}>
              Ta bort
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (deletePrivateIndividualStatus.status === Status.failed || exportStatus.status === Status.failed) {
    if (deletePrivateIndividualStatus.statusCode === 401 || exportStatus.statusCode === 401) {
      return <Navigate to="/session-utgangen" state={{ from: window.location.pathname }} />;
    }
    if (deletePrivateIndividualStatus.statusCode === 403 || exportStatus.statusCode === 403) {
      return <Navigate to="/atkomst-saknas" />;
    }
  }

  return (
    <>
      {selectedPrivateIndividual && (
        <FocusTrap isVisible={!!selectedPrivateIndividual} onEscape={handleOnClose}>
          <div className="modal d-block fade show" aria-labelledby="sign-modal-title" aria-modal="true" role="dialog">
            <div className="modal-dialog modal-dialog-centered">{renderDeleteModalContent()}</div>
          </div>
        </FocusTrap>
      )}
      {selectedPrivateIndividual && <div className="modal-backdrop fade show"></div>}

      {privateIndividuals && (
        <div className="private-individuals">
          <div className="mb-3">
            <button type="button" className="btn btn-primary" onClick={handleOnExportClick} disabled={exportStatus.status === Status.loading}>
              Exportera privatpersoner (.csv)
            </button>
          </div>
          {exportStatus.status === Status.failed && (
            <Alert show={true} heading="Kunde inte exportera privatpersoner, försök igen." closable={false} className="my-2" />
          )}
          <div className="mb-3 px-1">
            <label htmlFor="private-individual-filter" className="form-label">
              Fritextsök
            </label>
            <input
              type="text"
              id="private-individual-filter"
              className="form-control"
              placeholder="Epost eller exakt personnummer (ÅÅÅÅÅMMDDXXXX)"
              value={privateIndividualFilter}
              onChange={handleFilterChange}
              aria-label="Filtrera på e-post eller exakt personnummer (ÅÅÅÅÅMMDDXXXX)"
              autoComplete="off"
            />
          </div>

          <table className="table">
            <thead>
              <tr>
                <th scope="col" className="personnummer-column">
                  Personnummer
                </th>
                <th scope="col" className="email-column">
                  E-post
                </th>
                <th scope="col" className="verified-column">
                  Bekräftad
                </th>
                <th scope="col" className="menu-column">
                  <span className="visually-hidden">Meny</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredPrivateIndividuals.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    Inga privatpersoner matchar filtret
                  </td>
                </tr>
              ) : (
                filteredPrivateIndividuals.map((individual) => (
                  <tr key={individual.id} className="private-individual-row">
                    <td className="personnummer-column align-middle">{individual.personalIdentityNumber}</td>
                    <td className="email-column align-middle">{individual.email}</td>
                    <td className="verified-column align-middle">
                      {individual.isVerified && <Icon motif={IconMotif.Check} variant={IconVariant.CircleFilled} color={IconColor.Green600} altText="Bekräftad" />}
                    </td>
                    <td className="menu-column p-0">
                      <ListItemDropDownMenu
                        menuItems={[
                          {
                            type: MenuItemType.Delete,
                            label: 'Ta bort',
                            onClick: () => {
                              setModalTriggerElement(individual.id.toString());
                              setSelectedPrivateIndividual(individual);
                            },
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {deletePrivateIndividualStatus.status === Status.failed && (
            <Alert show={true} heading="Kunde inte ta bort privatpersonen" closable={false} className="mb-4" />
          )}
        </div>
      )}
    </>
  );
}

export default PrivateParticipations;